import React, { useState, useMemo } from "react";

import { CollapsibleSection } from "../CollapsibleSection";
import { formatDisplayDate } from "../../../helpers/dataHelper";
import { formatCurrency } from "../viewerHelpers";
import Field from "./Field";
import StatusBadge from "./StatusBadge";
import ExpandCollapseToggle from "./ExpandCollapseToggle";

// ===== Raw 810 Invoice Viewer =====

interface Raw810Invoice {
  invoiceNumber?: string;
  invoiceDate?: string;
  purchaseOrderNumber?: string;
  transactionType?: string;
  parties: Array<{
    type?: string;
    typeDesc?: string;
    name?: string;
    id?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
  }>;
  terms?: {
    termsType?: string;
    netDays?: number;
    discountPercent?: number;
    discountDays?: number;
  };
  lineItems: Array<{
    lineNumber?: string;
    quantity?: number;
    unit?: string;
    unitPrice?: number;
    productId?: string;
    description?: string;
    totalAmount?: number;
  }>;
  totalAmount?: number;
  lineItemCount?: number;
}

const parseRaw810Data = (data: unknown[]): Raw810Invoice | null => {
  // Find ST segment (transaction set header)
  const stSegment = data.find(
    (seg: unknown) =>
      (seg as { segment_id?: string }).segment_id === "ST" &&
      (seg as { transaction_set_identifier_code?: { code?: string } })
        .transaction_set_identifier_code?.code === "810",
  ) as {
    BIG?: {
      invoice_number?: string;
      date?: string;
      date_2?: string;
      purchase_order_number?: string;
      transaction_type_code?: { code?: string };
    };
    N1?: Array<{
      entity_identifier_code?: { code?: string; desc?: string };
      name?: string;
      identification_code?: string;
      N3?: Array<{ address_information?: string }>;
      N4?: {
        city_name?: string;
        state_or_province_code?: string;
        postal_code?: string;
      };
    }>;
    ITD?: Array<{
      terms_type_code?: string;
      terms_net_days?: number;
      terms_discount_percent?: number;
      terms_discount_days?: number;
    }>;
    IT1?: Array<{
      assigned_identification?: string;
      quantity_invoiced?: number;
      unit_or_basis_for_measurement_code?: { code?: string };
      unit_price?: number;
      product_service_id?: string;
    }>;
    TDS?: {
      amount?: number;
    };
    CTT?: {
      number_of_line_items?: number;
    };
  };

  if (!stSegment) return null;

  const big = stSegment.BIG;
  const parties = (stSegment.N1 || []).map((n1) => ({
    type: n1.entity_identifier_code?.code,
    typeDesc: n1.entity_identifier_code?.desc,
    name: n1.name,
    id: n1.identification_code,
    address: n1.N3?.[0]?.address_information,
    city: n1.N4?.city_name,
    state: n1.N4?.state_or_province_code,
    zip: n1.N4?.postal_code,
  }));

  const terms = stSegment.ITD?.[0]
    ? {
        termsType: stSegment.ITD[0].terms_type_code,
        netDays: stSegment.ITD[0].terms_net_days,
        discountPercent: stSegment.ITD[0].terms_discount_percent,
        discountDays: stSegment.ITD[0].terms_discount_days,
      }
    : undefined;

  const lineItems = (stSegment.IT1 || []).map((it1) => ({
    lineNumber: it1.assigned_identification,
    quantity: it1.quantity_invoiced,
    unit: it1.unit_or_basis_for_measurement_code?.code,
    unitPrice: it1.unit_price,
    productId: it1.product_service_id,
    totalAmount: (it1.quantity_invoiced || 0) * (it1.unit_price || 0),
  }));

  return {
    invoiceNumber: big?.invoice_number,
    invoiceDate: big?.date,
    purchaseOrderNumber: big?.purchase_order_number,
    transactionType: big?.transaction_type_code?.code,
    parties,
    terms,
    lineItems,
    totalAmount: stSegment.TDS?.amount,
    lineItemCount: stSegment.CTT?.number_of_line_items,
  };
};

const Raw810Viewer: React.FC<{ data: unknown[] }> = ({ data }) => {
  const [allExpanded, setAllExpanded] = useState(true);

  const invoice = useMemo(() => parseRaw810Data(data), [data]);

  if (!invoice) {
    return (
      <div className="p-4 text-sm text-gray-500">
        No invoice information found in this 810 file.
      </div>
    );
  }

  const formatFullAddress = (party: Raw810Invoice["parties"][0]) => {
    const parts = [party.address, party.city, party.state, party.zip].filter(
      Boolean,
    );
    return parts.length > 0 ? parts.join(", ") : "-";
  };

  return (
    <div className="flex flex-col h-full w-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center flex-wrap gap-2">
          <h2 className="text-xl font-semibold text-gray-900">810 Invoice</h2>
          <StatusBadge status="INVOICE" type="type" />
          <div className="flex-1" />
          <div>
            <span className="text-gray-500 text-sm">INVOICE #</span>
            <span className="ml-2 font-medium text-sm">
              {invoice.invoiceNumber || "-"}
            </span>
          </div>
          <div>
            <span className="text-gray-500 text-sm">PO #</span>
            <span className="ml-2 font-medium text-sm">
              {invoice.purchaseOrderNumber || "-"}
            </span>
          </div>
          <div>
            <span className="text-gray-500 text-sm">TOTAL</span>
            <span className="ml-2 font-medium text-green-600 text-sm">
              {formatCurrency(invoice.totalAmount)}
            </span>
          </div>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <ExpandCollapseToggle
            expanded={allExpanded}
            onToggle={setAllExpanded}
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 scrollbar-thin">
        <CollapsibleSection title="Invoice Details" defaultOpen={allExpanded}>
          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            }}
          >
            <Field label="Invoice Number" value={invoice.invoiceNumber} />
            <Field
              label="Invoice Date"
              value={formatDisplayDate(invoice.invoiceDate)}
            />
            <Field
              label="Purchase Order #"
              value={invoice.purchaseOrderNumber}
            />
            <Field label="Transaction Type" value={invoice.transactionType} />
            <Field
              label="Total Amount"
              value={formatCurrency(invoice.totalAmount)}
            />
            <Field label="Line Item Count" value={invoice.lineItemCount} />
          </div>
        </CollapsibleSection>

        {invoice.parties.length > 0 && (
          <CollapsibleSection
            title={`Parties (${invoice.parties.length})`}
            defaultOpen={allExpanded}
          >
            <div className="space-y-3">
              {invoice.parties.map((party, idx) => (
                <div key={idx} className="p-3 bg-white rounded border">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 rounded text-md font-medium bg-amber-100 text-amber-800">
                      {party.typeDesc || party.type || "Party"}
                    </span>
                    <span className="font-medium text-gray-900">
                      {party.name || "-"}
                    </span>
                  </div>
                  <div
                    className="grid gap-4 text-sm"
                    style={{
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(180px, 1fr))",
                    }}
                  >
                    <div>
                      <span className="text-sm text-gray-500">ID</span>
                      <div className="font-mono">{party.id || "-"}</div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Address</span>
                      <div>{formatFullAddress(party)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {invoice.terms && (
          <CollapsibleSection title="Payment Terms" defaultOpen={allExpanded}>
            <div
              className="grid gap-4"
              style={{
                gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
              }}
            >
              <Field label="Terms Type" value={invoice.terms.termsType} />
              <Field label="Net Days" value={invoice.terms.netDays} />
              {invoice.terms.discountPercent && (
                <Field
                  label="Discount %"
                  value={`${invoice.terms.discountPercent}%`}
                />
              )}
              {invoice.terms.discountDays && (
                <Field
                  label="Discount Days"
                  value={invoice.terms.discountDays}
                />
              )}
            </div>
          </CollapsibleSection>
        )}

        {invoice.lineItems.length > 0 && (
          <CollapsibleSection
            title={`Line Items (${invoice.lineItems.length})`}
            defaultOpen={allExpanded}
          >
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-sm text-gray-500 uppercase">
                    <th className="px-3 py-2 text-left">Line</th>
                    <th className="px-3 py-2 text-left">Product ID</th>
                    <th className="px-3 py-2 text-right">Quantity</th>
                    <th className="px-3 py-2 text-left">Unit</th>
                    <th className="px-3 py-2 text-right">Unit Price</th>
                    <th className="px-3 py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.lineItems.map((item, idx) => (
                    <tr key={idx} className="border-b border-gray-100">
                      <td className="px-3 py-2 text-md font-medium">
                        {item.lineNumber || idx + 1}
                      </td>
                      <td className="px-3 py-2 text-md font-mono">
                        {item.productId || "-"}
                      </td>
                      <td className="px-3 py-2 text-md text-right">
                        {item.quantity ?? "-"}
                      </td>
                      <td className="px-3 py-2 text-md">{item.unit || "-"}</td>
                      <td className="px-3 py-2 text-md text-right">
                        {formatCurrency(item.unitPrice)}
                      </td>
                      <td className="px-3 py-2 text-md text-right font-medium">
                        {formatCurrency(item.totalAmount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t border-gray-200">
                  <tr className="font-medium">
                    <td colSpan={5} className="px-3 py-2 text-right text-md">
                      Total:
                    </td>
                    <td className="px-3 py-2 text-right text-md text-green-600">
                      {formatCurrency(invoice.totalAmount)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CollapsibleSection>
        )}
      </div>
    </div>
  );
};

export default Raw810Viewer;
